import { useEffect, useState, type FormEvent } from "react";
import { builtIn } from "../emoji";
import { useLantern, useSession } from "../hooks";
import { Emoji } from "./Emoji";
import { Card, EmptyState, Field, Form, Page } from "./ui";
import { locale, t } from "../i18n";

const maxBytes = 128 << 10;
const types = ["image/png", "image/gif", "image/jpeg", "image/webp"];

// nameOf suggests an emoji's name from its file's: party-parrot.gif is
// party-parrot.
export function nameOf(file: string): string {
  return file
    .replace(/\.[^.]*$/, "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 100);
}

// EmojiView lists the workspace's custom emoji, and lets anyone but a
// guest add one, and remove one they added; an admin removes any.
export function EmojiView() {
  const session = useSession();
  const state = useLantern();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [name, setName] = useState("");
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const me = state.me;
  const guest = me?.role === "guest";

  useEffect(() => {
    void session.loadEmoji();
  }, [session]);
  useEffect(() => {
    if (!file) return setPreview("");
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const clean = name.trim().replace(/^:|:$/g, "");
  const problem = !file
    ? ""
    : !types.includes(file.type)
      ? t("An emoji is a PNG, GIF, JPEG or WebP image.")
      : file.size > maxBytes
        ? t("The image is {n} KB; an emoji is at most 128 KB. 128×128 pixels is plenty.", { n: Math.ceil(file.size / 1024) })
        : !clean
          ? ""
          : !/^[a-z0-9_-]{1,100}$/.test(clean)
            ? t("A name is lowercase letters, digits, _ and -.")
            : builtIn(clean) || state.emoji.some((e) => e.name === clean)
              ? t("There is already an emoji named :{name}:.", { name: clean })
              : "";

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!file || !clean || problem) return;
    setError("");
    setBusy(true);
    try {
      await session.api.addEmoji(clean, file);
      setFile(null);
      setName("");
      await session.loadEmoji();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function remove(n: string) {
    setError("");
    try {
      await session.api.removeEmoji(n);
      await session.loadEmoji();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const q = filter.trim().toLowerCase().replace(/^:|:$/g, "");
  const shown = state.emoji.filter((e) => !q || e.name.includes(q));
  return (
    <Page
      className="emoji-view"
      narrow
      title={t("Custom emoji")}
      description={t("Images {workspace} uses as emoji: in reactions, and as :name: in messages.", { workspace: state.workspace || t("the workspace") })}
    >
      {!guest && (
        <Card title={t("Add an emoji")} description={t("A square PNG, GIF, JPEG or WebP of up to 128 KB. A GIF keeps moving.")}>
          <Form onSubmit={add} className="emoji-add-form">
            <div className="emoji-add-row">
              <label className="emoji-drop" title={t("Choose an image")}>
                {preview ? <img src={preview} alt="" /> : <span aria-hidden>＋</span>}
                <input
                  type="file"
                  accept={types.join(",")}
                  aria-label={t("Image")}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setFile(f);
                    if (f && !name) setName(nameOf(f.name));
                    e.target.value = "";
                  }}
                />
              </label>
              <Field label={t("Name")} hint={clean ? t("Type :{name}: to use it.", { name: clean }) : t("What people type between colons.")}>
                <input value={name} onChange={(e) => setName(e.target.value.toLowerCase())} placeholder="partyparrot" spellCheck={false} maxLength={102} />
              </Field>
            </div>
            {(problem || error) && <p className="error">{problem || error}</p>}
            <div className="form-actions">
              <button type="submit" disabled={busy || !file || !clean || !!problem}>
                {busy ? t("Adding…") : t("Add emoji")}
              </button>
            </div>
          </Form>
        </Card>
      )}
      <Card
        title={t("{n} custom emoji", { n: state.emoji.length })}
        actions={state.emoji.length > 12 && <input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("Find an emoji")} aria-label={t("Find an emoji")} />}
      >
        {guest && error && <p className="error">{error}</p>}
        {state.emoji.length === 0 ? (
          <EmptyState icon="✨" title={t("No custom emoji yet")}>
            {guest ? t("Members add them here.") : t("Add the first: your team's logo, a mascot, an inside joke.")}
          </EmptyState>
        ) : (
          <ul className="rows emoji-list" aria-label={t("Custom emoji")}>
            {shown.map((e) => (
              <li key={e.name}>
                <span className="emoji-list-image">
                  <Emoji name={e.name} />
                </span>
                <span className="row-main">
                  <span className="row-title">:{e.name}:</span>
                  <span className="row-meta">
                    {t("Added by {name} on {date}", {
                      name: session.name(e.created_by),
                      date: new Date(e.created_at).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" }),
                    })}
                  </span>
                </span>
                {(e.created_by === me?.id || me?.role === "admin") && (
                  <button className="small danger" onClick={() => void remove(e.name)} aria-label={t("Remove :{name}:", { name: e.name })}>
                    {t("Remove")}
                  </button>
                )}
              </li>
            ))}
            {shown.length === 0 && <li className="quiet">{t("No emoji match “{q}”.", { q: filter.trim() })}</li>}
          </ul>
        )}
      </Card>
    </Page>
  );
}
